import { apiClient } from './client';
import {
  ExchangeRate,
  ExchangeRateFetchAttempt,
  ExchangeRateOptions,
  InvoiceBankCharge,
  ManualRateInput,
  SsclRate,
} from '../types/exchangeRate';

/** SSCL tax / exchange-rate payment overhaul (Phase 79) — staff-facing
 * read surface only. The actual rate is pushed by an external fetcher
 * script via a service-key-authenticated endpoint this app never calls
 * from the browser (see scripts/exchange-rate-fetcher). */
export const getCurrentExchangeRate = async (): Promise<ExchangeRate | null> => {
  const { data } = await apiClient.get('/exchange-rate/current');
  return data.data;
};

export const getExchangeRateHistory = async (): Promise<ExchangeRate[]> => {
  const { data } = await apiClient.get('/exchange-rate/history');
  return data.data;
};

export const getExchangeRateFetchLog = async (): Promise<ExchangeRateFetchAttempt[]> => {
  const { data } = await apiClient.get('/exchange-rate/log');
  return data.data;
};

export const getSsclRate = async (): Promise<{ current: SsclRate | null; history: SsclRate[] }> => {
  const { data } = await apiClient.get('/master-data/sscl-rate');
  return data.data;
};

// SSCL divisor-based calculation correction — both the displayed/reference
// percentage and the real calculation input (calculationDivisor) are
// published together as one versioned row.
export const publishSsclRate = async (percentage: number, calculationDivisor: number): Promise<SsclRate> => {
  const { data } = await apiClient.post('/master-data/sscl-rate', { percentage, calculationDivisor });
  return data.data;
};

export const getInvoiceBankCharge = async (): Promise<{ current: InvoiceBankCharge | null; history: InvoiceBankCharge[] }> => {
  const { data } = await apiClient.get('/master-data/invoice-bank-charge');
  return data.data;
};

export const publishInvoiceBankCharge = async (amountUsd: number): Promise<InvoiceBankCharge> => {
  const { data } = await apiClient.post('/master-data/invoice-bank-charge', { amountUsd });
  return data.data;
};

/** Phase 104 — the rates a user can choose for a payment, invoice or receipt. */
export const getExchangeRateOptions = async (): Promise<ExchangeRateOptions> => {
  const { data } = await apiClient.get('/exchange-rate/options');
  return data.data;
};

/** Phase 104 — the automatic Nations Trust rate in force at a past moment. */
export const getAutomaticRateAt = async (at: string): Promise<ExchangeRate | null> => {
  const { data } = await apiClient.get('/exchange-rate/effective', { params: { at } });
  return data.data;
};

/** Phase 104 — publish a manual (optionally backdated) rate. */
export const publishManualRate = async (input: ManualRateInput): Promise<ExchangeRate> => {
  const { data } = await apiClient.post('/exchange-rate/manual', input);
  return data.data;
};

/** Phase 104 — correct a manual rate nothing uses yet. */
export const updateManualRate = async (id: number, input: ManualRateInput): Promise<ExchangeRate> => {
  const { data } = await apiClient.put(`/exchange-rate/manual/${id}`, input);
  return data.data;
};
